/**
 * Le categorie della guida, ricavate dai canali.
 *
 * ## Perché sta nel dominio e non nella schermata
 *
 * Perché è un conto, e i conti si provano senza un browser. Ma soprattutto perché è un conto su
 * **dodicimila canali**: farlo nel corpo di un componente lo rifarebbe a ogni freccia, ed è il costo
 * esatto che la griglia virtualizzata esiste per evitare. Qui è una funzione pura, la schermata la
 * memorizza una volta per catalogo, e nessuno può sbagliarsi.
 *
 * ## Il nome arriva da un'altra chiamata
 *
 * `Channel` porta `categoryId`, non l'etichetta. Per un po' ho usato l'identificativo **come** nome,
 * e su una installazione vera quello è un UUID: la colonna della guida scriveva
 * `9f9ddebc-2c62-4d61-…` al posto di «Sport». Il nome sta in `get_live_categories`, e nessuno la
 * faceva.
 *
 * Quindi qui si passa una mappa `id → nome`. Una categoria che la mappa non conosce **non si
 * inventa**: si mostra come «categoria senza nome», che è un'informazione — mentre l'UUID era un
 * difetto travestito da dato.
 *
 * Le due voci che l'app aggiunge — tutti e preferiti — sono sue, e quelle hanno una chiave i18n.
 * `translated` dice quale delle due cose è l'etichetta: senza quel campo la schermata dovrebbe
 * riconoscere le voci speciali dal nome, cioè decidere due volte la stessa cosa.
 */

import { compareCatalogNames } from './catalogSort.js';
import type { Channel } from './guide.js';

export const CATEGORY_ALL = '__all__';
export const CATEGORY_FAVORITES = '__favorites__';

export interface GuideCategory {
  readonly id: string;
  /** Il nome del fornitore, oppure la chiave i18n quando `translated` è vero. */
  readonly label: string;
  readonly count: number;
  readonly translated: boolean;
}

export const CATEGORY_UNNAMED = 'guide.category.unnamed';

export function buildGuideCategories(
  channels: ReadonlyArray<Channel>,
  favoriteIds: ReadonlySet<string>,
  /** I nomi, per identificativo. Vuota finché la chiamata non è tornata. */
  names: ReadonlyMap<string, string> = new Map(),
): ReadonlyArray<GuideCategory> {
  /** La posizione di ogni categoria nell'elenco che il gateway ha mandato: è l'ordine del pannello. */
  const order = new Map<string, number>();
  let at = 0;
  for (const id of names.keys()) {
    order.set(id, at);
    at += 1;
  }

  const counts = new Map<string, number>();
  for (const channel of channels) {
    counts.set(channel.categoryId, (counts.get(channel.categoryId) ?? 0) + 1);
  }

  const fromProvider: GuideCategory[] = [...counts.entries()]
    .map(([id, count]) => {
      const name = names.get(id);
      return name === undefined
        ? // Senza nome si dice «senza nome», e si traduce: l'identificativo grezzo non è un ripiego,
          // è l'errore che stiamo togliendo.
          { id, label: CATEGORY_UNNAMED, count, translated: true }
        : { id, label: name, count, translated: false };
    })
    /*
      **L'ordine è quello che decide il pannello**, e l'app non lo rifà.

      `categories.position` è il campo che l'operatore trascina in elenco, e `get_live_categories` lo
      rispetta: la mappa dei nomi arriva **già ordinata**, e le mappe conservano l'ordine di
      inserimento. Qui si riordina per la posizione dentro quella mappa, cioè per quel che ha deciso
      chi vende.

      Ci ero arrivato per due strade sbagliate. Prima ordinavo per identificativo — con gli UUID è
      casuale in modo perfettamente uniforme. Poi alfabeticamente per nome, e su un catalogo vero è
      peggio: i nomi cominciano **tutti con un'emoji** (`⚽️ | DAZN | SERIE A`, `📽 | SKY | CINEMA
      SAT`), quindi `localeCompare` ordinava per punto di codice Unicode dell'emoji — un ordine che
      per chi legge non è nessun ordine, e che somiglia a un ordine rovesciato.

      Chi non è nella mappa va **in fondo**, per nome letto come si legge (`catalogSort.ts`): sono le
      categorie senza nome, e non hanno una posizione da rispettare.
    */
    .sort((a, b) => {
      const left = order.get(a.id);
      const right = order.get(b.id);
      if (left !== undefined && right !== undefined) return left - right;
      if (left !== undefined) return -1;
      if (right !== undefined) return 1;
      return compareCatalogNames(a.label, b.label);
    });

  return [
    { id: CATEGORY_ALL, label: 'guide.category.all', count: channels.length, translated: true },
    {
      id: CATEGORY_FAVORITES,
      label: 'guide.category.favorites',
      count: favoriteIds.size,
      translated: true,
    },
    ...fromProvider,
  ];
}

/**
 * I canali di una categoria.
 *
 * `CATEGORY_FAVORITES` **non** è una categoria del fornitore e non si cerca fra i `categoryId`: se lo
 * fosse, un fornitore che chiamasse una sua categoria `__favorites__` la dirotterebbe. I due
 * identificativi hanno i trattini bassi doppi proprio per non collidere con niente di reale, ma la
 * difesa vera è che si controllano **prima**.
 */
export function channelsOfCategory(
  channels: ReadonlyArray<Channel>,
  categoryId: string,
  favoriteIds: ReadonlySet<string>,
): ReadonlyArray<Channel> {
  if (categoryId === CATEGORY_ALL) return channels;
  if (categoryId === CATEGORY_FAVORITES) return channels.filter((channel) => favoriteIds.has(channel.id));
  return channels.filter((channel) => channel.categoryId === categoryId);
}

/**
 * Sposta il cursore fra le categorie, fermandosi ai bordi.
 *
 * Ai bordi si **resta**: una colonna che avvolge fa premere due volte per tornare dove si era, e su
 * un telecomando si sente. È la stessa regola del menu e della griglia dei preferiti — se una delle
 * tre avvolgesse, la differenza si noterebbe senza sapere perché.
 */
export function moveCategoryCursor(current: number, delta: number, total: number): number {
  const next = current + delta;
  return next < 0 || next >= total ? current : next;
}
